// Custom DS-Table column rendering the Quote Expiry date, red once the quote has expired (002 D7).
// Table.TextColumn cannot style its own cell text, so this follows the established custom-column pattern
// (see QuoteStatusColumn) and imports the two cell/column-support helpers from the dist internals — the
// accepted web-front convention.
//
// The expired/live decision is NOT made here: it arrives on the row as `quoteExpired`, computed in
// opportunityTableRows against the same non-UTC date path the label uses, so the calendar date never
// drifts with the viewer's timezone and the rule is covered by that module's own tests.
//
// Display-only. Per the quote lifecycle, "Expired" is a logged event in V1 with no automatic status
// transition, so nothing here writes a quote status.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import Styled from 'styled-components';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';

// The error red named by 002 D7, and already used by MissingDatePopover's accent and label.
export const EXPIRED_COLOR = '#db0900';

export interface QuoteExpiryColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// px: the default width (and what a grip double-click resets to) and the drag lower bound.
	size?: number;
	minSize?: number;
}

const ExpiryText = Styled.span<{$expired: boolean}>`
	color: ${props => (props.$expired ? EXPIRED_COLOR : 'inherit')};
`;

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuoteExpiryColumn = (_: QuoteExpiryColumnType): null => null;

QuoteExpiryColumn.toColumnDef = (child: React.ReactElement<QuoteExpiryColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {quoteExpiryLabel, quoteExpired} = row.original;
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-quote-expiry-cell">
				{/* data-expired is the stable hook for the RTL test; the colour itself is what the user sees,
				    and what the E2E assertion checks. */}
				<ExpiryText $expired={quoteExpired} data-expired={quoteExpired ? 'true' : undefined}>
					{quoteExpiryLabel}
				</ExpiryText>
			</StyledCell>
		);
	},
});

export default QuoteExpiryColumn;
